/**
 * PostHog analytics — client setup for the ETP Member Portal.
 *
 * One PostHog project for the whole AlexAI ecosystem. We identify by the Clerk
 * user id (which equals the Supabase user_id used by Pro/Gov), so a person is the
 * same human across every *.alexepstein.ai app. Per-app attribution comes from the
 * `app` super-property, registered once at init — never inferred. This app is
 * always `portal`.
 *
 * PRIVACY: capture who / what / journey, but NEVER member-private content. Session
 * recording and autocapture are off so nothing typed (search box, forms) can leak;
 * only explicit funnel events + pageviews are sent.
 *
 * This is a Vite SPA (no Next.js rewrites), so the `/ingest` reverse proxy lives in
 * the Caddyfile. When VITE_POSTHOG_KEY is unset (e.g. local dev) this is fully inert.
 */
import posthog from "posthog-js";

export const APP = "portal";
export const POSTHOG_ENABLED = !!import.meta.env.VITE_POSTHOG_KEY;

let initialized = false;

export function initPostHog() {
  if (initialized || typeof window === "undefined" || !POSTHOG_ENABLED) return;
  posthog.init(import.meta.env.VITE_POSTHOG_KEY as string, {
    // Reverse-proxy ingestion through Caddy (see Caddyfile) so events look
    // first-party and survive ad-blockers. Mirrors the Pro frontend's /ingest rewrite.
    api_host: "/ingest",
    ui_host: "https://us.posthog.com",
    // SPA: client-side route changes don't fire pageviews, so we capture manually.
    capture_pageview: false,
    capture_pageleave: true,
    // Only materialize person profiles once a user is identified (cheaper + cleaner).
    person_profiles: "identified_only",
    // Hard privacy guarantees:
    disable_session_recording: true,
    autocapture: false,
  });
  posthog.register({ app: APP }); // super-property attached to every event
  if (typeof window !== "undefined") {
    (window as unknown as { posthog: typeof posthog }).posthog = posthog; // expose for debugging
  }
  initialized = true;
}

/** Capture a funnel event. No-op when PostHog is disabled. Never pass private content. */
export function capture(event: string, properties: Record<string, unknown> = {}) {
  if (!POSTHOG_ENABLED) return;
  posthog.capture(event, { app: APP, ...properties });
}

// --- Internal/test-account detection (mirrors experiments/.../account_classifier.py) ---
// Stamped as an `is_internal` person property so Checkly E2E, the internal team, and
// Tom's test aliases can be filtered out of every funnel with one toggle.
const INTERNAL_DOMAINS = new Set([
  "alexepstein.com",
  "energyfreedom.org",
  "eventignite.com",
  "eventignite.co.uk",
]);
const INTERNAL_EMAILS = new Set([
  "tom@tomwalczak.com",
  "tomwalczak90@gmail.com",
  "marianne.r.epstein@gmail.com",
  "oleksandr.halik@gmail.com",
  "tom+checkly@tomwalczak.com",
  "tom+agent@tomwalczak.com",
]);

export function isInternalEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const e = email.toLowerCase();
  if (INTERNAL_EMAILS.has(e)) return true;
  // Tom's +alias test accounts (tom+x@, thomas+x@).
  if (/^(tom|thomas)\+[^@]*@/.test(e)) return true;
  const domain = e.split("@")[1] || "";
  return INTERNAL_DOMAINS.has(domain);
}

export { posthog };
